import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react'
import styles from './SongView.module.scss'
import UserContext from '../context/UserContext'
import useLearnerContext from '../hooks/useLearnerContext'
import languages, { getLanguageAcronym } from '../languages'
import flags from '../flags'
import voices from '../speech/voices'
import { WordLookupProvider, useWordLookup } from './WordLookup'
import { cleanWord } from './SpokenText'
import { isSpotifyConfigured, isSpotifyConnected, loginWithSpotify, logoutSpotify, fetchNowPlaying, redirectUri } from '../spotify/spotify'
import { findLyrics, parseLrc, lineAt } from '../lyrics/lrclib'

import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import MusicNoteIcon from '@mui/icons-material/MusicNote'
import SchoolIcon from '@mui/icons-material/School'
import RefreshIcon from '@mui/icons-material/Refresh'

// ─────────────────────────────────────────────────────────────────────────────
// Song mode (β). Whatever is playing on the learner's Spotify — phone, desktop,
// anywhere — shows up here with its lyrics, the current line lit as the song
// plays and the words inside it lighting in turn. Tap a word for its meaning.
// "Save as class" hands the lyrics to the meeting importer, which translates
// them and turns them into a class you can practise and assign.
//
// Timing comes from Spotify's playback position (polled, extrapolated between
// polls); the words come from LRCLIB, a free open lyrics database. Line timing
// is real; word timing within a line is estimated unless the entry carries it.
// ─────────────────────────────────────────────────────────────────────────────

const THEME_KEY = 'lessonTheme'
const readTheme = () => { try { const t = localStorage.getItem(THEME_KEY); return t === 'light' || t === 'dark' ? t : 'dark' } catch { return 'dark' } }
const POLL_MS = 2000
const TICK_MS = 120
const fmt = (ms) => { const s = Math.max(0, Math.floor(ms / 1000)); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` }

// Word timings for one line: the entry's own when it has them, otherwise the
// line's span shared out by word length (the same trick the drills use).
const wordTimes = (line, nextT) => {
  const end = nextT != null ? nextT : line.t + 4000
  if (line.words && line.words.some((w) => w.t != null)) {
    let last = line.t
    return line.words.map((w, i) => {
      const t = w.t != null ? w.t : last
      last = t
      const n = line.words[i + 1]
      return { text: w.text, t, end: n && n.t != null ? n.t : end }
    })
  }
  const ws = line.text.split(/\s+/).filter(Boolean)
  const weights = ws.map((w) => Math.max(1, cleanWord(w).length) + 1)
  const total = weights.reduce((a, b) => a + b, 0) || 1
  let t = line.t
  return ws.map((w, i) => { const d = ((end - line.t) * weights[i]) / total; const out = { text: w, t, end: t + d }; t += d; return out })
}

function Lyrics({ lines, posMs, plain, notFound, instrumental, loading }) {
  const { lookup } = useWordLookup()
  const cur = lineAt(lines, posMs)
  const refs = useRef([])
  useEffect(() => {
    const el = refs.current[cur]
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [cur])

  if (loading) return <div className={styles.muted}>Looking up the lyrics…</div>
  if (instrumental) return <div className={styles.muted}>An instrumental — nothing to read along to.</div>
  if (!lines.length) {
    if (plain) {
      return (
        <div className={styles.plain}>
          <div className={styles.muted}>No synced lyrics for this one, so here they are unsynced. Tap any word.</div>
          {plain.split(/\r?\n/).map((l, i) => (
            <div key={i} className={styles.line}>
              {l.split(/(\s+)/).map((tok, k) => (/\s+/.test(tok) || !tok ? tok : <span key={k} className={styles.word} onClick={(e) => lookup(cleanWord(tok), e, l)}>{tok}</span>))}
            </div>
          ))}
        </div>
      )
    }
    return <div className={styles.muted}>{notFound ? 'LRCLIB has no lyrics for this track yet.' : 'Play a song and its lyrics will appear here.'}</div>
  }

  return (
    <div className={styles.lines}>
      {lines.map((line, i) => {
        const state = i < cur ? styles.past : i === cur ? styles.current : styles.future
        const words = i === cur ? wordTimes(line, lines[i + 1] && lines[i + 1].t) : null
        return (
          <div key={`${line.t}-${i}`} ref={(el) => { refs.current[i] = el }} className={`${styles.line} ${state}`}>
            {words
              ? words.map((w, k) => {
                  const on = posMs >= w.t && posMs < w.end
                  const done = posMs >= w.end
                  return (
                    <React.Fragment key={k}>
                      <span className={`${styles.word} ${on ? styles.wordOn : ''} ${done ? styles.wordDone : ''}`} onClick={(e) => lookup(cleanWord(w.text), e, line.text)}>{w.text}</span>{' '}
                    </React.Fragment>
                  )
                })
              : line.text.split(/(\s+)/).map((tok, k) => (/\s+/.test(tok) || !tok ? tok : <span key={k} className={styles.word} onClick={(e) => lookup(cleanWord(tok), e, line.text)}>{tok}</span>))}
            {line.text ? null : <span className={styles.rest}>♪</span>}
          </div>
        )
      })}
    </div>
  )
}

export default function SongView({ voiceSpeed, onClose, onSaveAsClass }) {
  const { username } = useContext(UserContext)
  const { learning } = useLearnerContext(username)
  const rate = (typeof voiceSpeed === 'number' && voiceSpeed > 0) ? voiceSpeed : 0.9
  const [theme, setTheme] = useState(readTheme)
  const toggleTheme = () => setTheme((t) => { const next = t === 'dark' ? 'light' : 'dark'; try { localStorage.setItem(THEME_KEY, next) } catch { /* private mode */ } return next })

  // Which language the song is in: the profile's languages as chips, plus a
  // full list behind "other". Drives the word lookup and its voice.
  const learningCodes = useMemo(() => (learning || []).map((l) => l && (l.code || getLanguageAcronym(l.name))).filter(Boolean), [learning])
  const [lang, setLang] = useState('')
  const langCode = lang || learningCodes[0] || 'eng'
  const langName = languages[langCode] || langCode

  const configured = isSpotifyConfigured()
  const [connected, setConnected] = useState(isSpotifyConnected())
  const [now, setNow] = useState(null)      // normalised now-playing, or null
  const [err, setErr] = useState('')
  const [posMs, setPosMs] = useState(0)
  const [lyr, setLyr] = useState({ lines: [], plain: '', notFound: false, instrumental: false, loading: false })
  const [loginErr, setLoginErr] = useState('')

  // Poll Spotify while open. A 429 backs off; a 401 drops to "connect".
  const poll = useCallback(async () => {
    const r = await fetchNowPlaying()
    if (r && r.error) {
      if (r.error === 'not-connected') { setConnected(false); setNow(null) }
      else if (r.error !== 'rate-limited') setErr(r.error)
      return
    }
    setErr('')
    setNow((prev) => (prev && r && prev.id === r.id && prev.isPlaying === r.isPlaying && Math.abs((prev.progressMs + (Date.now() - prev.at)) - r.progressMs) < 1500 ? { ...prev, progressMs: r.progressMs, at: r.at } : r))
  }, [])
  useEffect(() => {
    if (!connected) return
    let alive = true
    const run = async () => { if (alive) await poll() }
    run()
    const id = setInterval(run, POLL_MS)
    return () => { alive = false; clearInterval(id) }
  }, [connected, poll])

  // Extrapolate the position between polls so the highlight moves smoothly.
  useEffect(() => {
    if (!now) { setPosMs(0); return }
    const tick = () => setPosMs(now.progressMs + (now.isPlaying ? Date.now() - now.at : 0))
    tick()
    const id = setInterval(tick, TICK_MS)
    return () => clearInterval(id)
  }, [now])

  // New track → fetch its lyrics (cached per track on this device). The track
  // details are read through a ref so the effect keys on the id alone — `now`
  // itself changes on every poll as the position moves.
  const trackId = now && now.id
  const nowRef = useRef(now)
  nowRef.current = now
  useEffect(() => {
    const t = nowRef.current
    if (!trackId || !t) return
    let alive = true
    setLyr({ lines: [], plain: '', notFound: false, instrumental: false, loading: true })
    findLyrics({ artist: t.artists[0] || '', title: t.title, album: t.album, durationSec: t.durationMs / 1000, cacheKey: t.id })
      .then((r) => { if (alive) setLyr({ lines: parseLrc(r.synced), plain: r.plain, notFound: r.notFound, instrumental: r.instrumental, loading: false }) })
      .catch(() => { if (alive) setLyr({ lines: [], plain: '', notFound: true, instrumental: false, loading: false }) })
    return () => { alive = false }
  }, [trackId])

  const connect = async () => { setLoginErr(''); try { await loginWithSpotify() } catch (e) { setLoginErr(e.message) } }
  const disconnect = () => { logoutSpotify(); setConnected(false); setNow(null) }
  const saveAsClass = () => {
    if (!now || !onSaveAsClass) return
    const text = lyr.plain || lyr.lines.map((l) => l.text).filter(Boolean).join('\n')
    onSaveAsClass({ title: `♪ ${now.title} — ${now.artists.join(', ')}`, text, languageName: langName })
  }

  const root = `${styles.view} ${styles[theme]}`
  return (
    <div className={root}>
      <div className={styles.topBtns}>
        <button className={styles.iconBtn} onClick={toggleTheme} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>{theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}</button>
        <button className={styles.iconBtn} onClick={onClose} title="Close"><CloseIcon /></button>
      </div>
      <div className={styles.wrap}>
        <div className={styles.label}><MusicNoteIcon fontSize="inherit" /> Song mode · β</div>

        {!configured ? (
          <div className={styles.card}>
            <h2 className={styles.title}>Spotify isn't set up yet</h2>
            {(() => {
              // Spotify refuses "localhost" as a redirect URI; only the loopback
              // address works without HTTPS. So show the address that will
              // actually be accepted, and say so when the tab is on localhost.
              const origin = typeof window !== 'undefined' ? window.location.origin : ''
              const onLocalhost = /\/\/localhost(:|$)/.test(origin)
              const uri = `${onLocalhost ? origin.replace('//localhost', '//127.0.0.1') : origin}/`
              return (
                <>
                  <p className={styles.muted}>
                    1. Create an app in the Spotify developer dashboard.<br />
                    2. Put its Client ID in <code>REACT_APP_SPOTIFY_CLIENT_ID</code> in the frontend's <code>.env</code> and restart the dev server.<br />
                    3. Register <code>{uri}</code> as a redirect URI on that app.<br />
                    4. Add your Spotify account under the app's user management (development mode).
                  </p>
                  {onLocalhost ? <p className={styles.err}>You're on localhost, which Spotify won't accept. Open this app at <code>{uri}</code> instead — same server, different address.</p> : null}
                </>
              )
            })()}
          </div>
        ) : !connected ? (
          <div className={styles.card}>
            <h2 className={styles.title}>Read along with whatever you're playing</h2>
            <p className={styles.muted}>Connect your Spotify account and this page follows what's playing on any of your devices — the lyrics scroll with the song, the current line lights up, and every word is a tap from its meaning. Reading playback works on free and Premium accounts.</p>
            <button className={styles.primary} onClick={connect}>Connect Spotify</button>
            {loginErr ? <div className={styles.err}>{loginErr}</div> : null}
            {/* "redirect_uri: Not matching configuration" means this exact
                string is not among the app's redirect URIs. Showing it here
                makes that a one-glance comparison instead of a guess. */}
            {(() => {
              const uri = redirectUri()
              const onLocalhost = /\/\/localhost(:|$)/.test(uri)
              return (
                <p className={styles.muted} style={{ fontSize: '0.8rem' }}>
                  This page will send <code>{uri}</code> as its redirect URI. It must appear, character for character, in your Spotify app's settings.
                  {onLocalhost ? ' Spotify rejects localhost, so open this app at the 127.0.0.1 address instead — same server, and then this line will match.' : ''}
                </p>
              )
            })()}
          </div>
        ) : (
          <>
            <div className={styles.nowRow}>
              {now && now.art ? <img className={styles.art} src={now.art} alt="" /> : <div className={`${styles.art} ${styles.artEmpty}`}><MusicNoteIcon /></div>}
              <div className={styles.nowBody}>
                {now ? (
                  <>
                    <div className={styles.nowTitle}>{now.title}</div>
                    <div className={styles.nowArtist}>{now.artists.join(', ')}{now.album ? ` · ${now.album}` : ''}</div>
                    <div className={styles.progress}><div className={styles.progressFill} style={{ width: `${now.durationMs ? Math.min(100, (posMs / now.durationMs) * 100) : 0}%` }} /></div>
                    <div className={styles.times}>{fmt(posMs)} / {fmt(now.durationMs)}{now.isPlaying ? '' : ' · paused'}</div>
                  </>
                ) : (
                  <div className={styles.muted}>Nothing playing. Start a song on Spotify — phone, desktop, anywhere — and it appears here.</div>
                )}
                {err ? <div className={styles.err}>Spotify: {err}</div> : null}
              </div>
              <div className={styles.nowActions}>
                <button className={styles.ghost} onClick={poll} title="Check now"><RefreshIcon fontSize="small" /></button>
                <button className={styles.ghost} onClick={disconnect} title="Disconnect Spotify">Disconnect</button>
              </div>
            </div>

            <div className={styles.langRow}>
              <span className={styles.langLabel}>Song language</span>
              {learningCodes.map((c) => (
                <button key={c} className={`${styles.chip} ${c === langCode ? styles.chipOn : ''}`} onClick={() => setLang(c)}>{flags[c] || '🌐'} {languages[c] || c}</button>
              ))}
              <select className={styles.select} value={learningCodes.includes(langCode) ? '' : langCode} onChange={(e) => setLang(e.target.value)}>
                <option value="">other…</option>
                {Object.entries(languages).filter(([c]) => !learningCodes.includes(c)).map(([c, n]) => <option key={c} value={c}>{n}</option>)}
              </select>
            </div>

            <WordLookupProvider lang={langCode} langName={langName} voice={voices[langCode] || voices.eng} rate={rate}>
              <div className={styles.lyricsPane}>
                <Lyrics lines={lyr.lines} posMs={posMs} plain={lyr.plain} notFound={lyr.notFound} instrumental={lyr.instrumental} loading={lyr.loading} />
              </div>
            </WordLookupProvider>

            <div className={styles.footer}>
              <button className={styles.primary} onClick={saveAsClass} disabled={!now || (!lyr.plain && !lyr.lines.length)} title="Hand the lyrics to the class importer — it translates them and builds vocabulary">
                <SchoolIcon fontSize="small" /> Save as class
              </button>
              <span className={styles.muted}>Lyrics from LRCLIB · line timing is real, word timing within a line is estimated{lyr.lines.some((l) => l.words) ? ' (this entry has word timing)' : ''}.</span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
